import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@fc/dialog';
import {
  FCEvents,
  CartAddPendingClearEvent,
  CartAddPendingEvent,
  CartSectionsRenderedEvent,
  getCartEventData,
  isCartAddPendingEvent,
  isCartAddSuccessEvent,
} from '@fc/events';
import { patchCartSectionMarkup } from '@fc/cart';
import { cartPerformance } from '@fc/metrics';
import { sectionManager } from '@fc/sections';

const SHOPIFY_SECTION_PREFIX = 'shopify-section-';
const PENDING_TIMEOUT_MS = 12000;
const IDLE_WARM_TIMEOUT_MS = 2500;
const CART_UPSELL_RECOMMENDATIONS_SELECTOR = 'fc-recommendations.cart-drawer-upsells';
const CART_DRAWER_AJAX_SECTION_ID = 'cart-drawer-ajax';
const ACCELERATED_CHECKOUT_SELECTOR = '.additional-checkout-buttons';
const DEFERRED_ACCELERATED_CHECKOUT_SELECTOR = `${ACCELERATED_CHECKOUT_SELECTOR}[data-deferred-accelerated-checkout]`;
const ACCELERATED_CHECKOUT_HYDRATION_TIMEOUT_MS = 1500;

class CartDrawerComponent extends DialogComponent {
  #openMetricDepth = 0;
  #pendingAdd = null;
  #pendingClearTimer = null;
  #idleWarmHandle = null;
  #deferredAcceleratedCheckoutPromise = null;
  #hasRequestedDeferredAcceleratedCheckout = false;

  connectedCallback() {
    super.connectedCallback();
    this.addManagedEventListener(document, CartAddPendingEvent.eventName, this.#handleCartAddPending);
    this.addManagedEventListener(document, CartAddPendingClearEvent.eventName, this.#handleCartAddPendingClear);
    this.addManagedEventListener(document, FCEvents.cartFail, this.#handleCartAddPendingClear);
    this.addManagedEventListener(document, CartSectionsRenderedEvent.eventName, this.#handleCartSectionsRendered);
    this.addManagedEventListener(this, DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addManagedEventListener(this, DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addManagedEventListener(this.refs.cartDrawerTrigger, 'pointerdown', this.#handleWarmIntent, { passive: true });
    this.addManagedEventListener(this.refs.cartDrawerTrigger, 'pointerenter', this.#handleWarmIntent, { passive: true });
    this.addManagedEventListener(this.refs.cartDrawerTrigger, 'touchstart', this.#handleWarmIntent, { passive: true });
    this.addManagedEventListener(this.refs.cartDrawerTrigger, 'focusin', this.#handleWarmIntent);
    this.#queueIdleWarm();
    this.#syncTriggerExpanded();
  }

  disconnectedCallback() {
    this.#cancelIdleWarm();
    this.#clearPendingState({ closeIfOpened: false, reason: 'cart-drawer-disconnect' });
    super.disconnectedCallback();
  }

  #handleCartAddPending = (event) => {
    if (!this.hasAttribute('auto-open')) return;
    if (!isCartAddPendingEvent(event)) return;

    const data = getCartEventData(event);
    const wasOpen = this.refs.dialog?.open === true;
    const suppressAutoOpen = data.suppressCartAutoOpen === true;
    this.#pendingAdd = {
      operationId: this.#operationIdFromEvent(event),
      sourceId: event.detail?.sourceId || null,
      openedByDrawer: !suppressAutoOpen && !wasOpen,
      suppressAutoOpen,
    };
    this.#setPendingState(true, data);
    this.#schedulePendingFallback();
    if (suppressAutoOpen) return;

    this.#openAndBindInstallments('add:drawer-pending-open', { reason: 'cart-add-pending' });
  };

  #handleCartAddPendingClear = (event) => {
    if (!this.#pendingAdd) return;
    if (!this.#matchesPendingOperation(event)) return;

    const data = getCartEventData(event);
    this.#clearPendingState({
      closeIfOpened: true,
      reason: data.aborted ? 'cart-add-abort' : data.didError ? 'cart-add-error' : 'cart-add-clear',
    });
  };

  #handleCartSectionsRendered = (event) => {
    if (!isCartAddSuccessEvent(event)) return;
    if (!this.#matchesRenderedSection(event)) return;
    if (!this.#matchesPendingOperation(event)) return;

    this.#warmRecommendations('cart-add-rendered');
    if (!this.hasAttribute('auto-open')) return;

    const shouldOpen = this.#pendingAdd?.suppressAutoOpen !== true;
    this.#clearPendingState({ closeIfOpened: false, reason: 'cart-add-rendered' });
    if (!shouldOpen) return;

    this.#requestDeferredAcceleratedCheckout();
    this.#openAndBindInstallments('add:drawer-open', { reason: 'cart-add-rendered' });
  };

  open() {
    this.#openAndBindInstallments('drawer:open', { reason: 'manual-open' });
  }

  showDialog(detail = {}) {
    return cartPerformance.measure(this.#openMetricDepth > 0 ? null : 'drawer:open', () => this.#showDialog(detail));
  }

  #showDialog(detail = {}) {
    super.showDialog(detail);

    if (this.refs.dialog?.open) {
      this.#syncTriggerExpanded(true);
      this.#bindInstallmentsCloseAction();
    }
  }

  close() {
    this.closeDialog();
  }

  #handleDialogOpen = () => {
    this.#syncTriggerExpanded(true);
    this.#requestDeferredAcceleratedCheckout();
    if (this.#isDesignMode()) this.#warmRecommendations('cart-design-mode-open');
  };

  #handleDialogClose = () => {
    this.#syncTriggerExpanded(false);
  };

  #handleWarmIntent = () => {
    this.#warmRecommendations('cart-trigger-intent');
    this.#requestDeferredAcceleratedCheckout();
  };

  #queueIdleWarm() {
    if (this.#isDesignMode()) {
      queueMicrotask(() => {
        if (!this.isConnected || !this.#hasCartItems()) return;

        this.#warmRecommendations('cart-design-mode-eager');
      });
      return;
    }

    const queue = () => {
      if (!this.isConnected || this.#idleWarmHandle) return;

      const callback = () => {
        this.#idleWarmHandle = null;
        if (!this.isConnected || !this.#hasCartItems()) return;

        this.#warmRecommendations('cart-non-empty-idle');
      };

      if (typeof window.requestIdleCallback === 'function') {
        this.#idleWarmHandle = {
          type: 'idle',
          id: window.requestIdleCallback(callback, { timeout: IDLE_WARM_TIMEOUT_MS }),
        };
        return;
      }

      this.#idleWarmHandle = {
        type: 'timeout',
        id: setTimeout(callback, IDLE_WARM_TIMEOUT_MS),
      };
    };

    if (document.readyState === 'complete') {
      queue();
      return;
    }

    this.addManagedEventListener(window, 'load', queue, { once: true });
  }

  #cancelIdleWarm() {
    if (!this.#idleWarmHandle) return;

    if (this.#idleWarmHandle.type === 'idle' && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(this.#idleWarmHandle.id);
    } else {
      clearTimeout(this.#idleWarmHandle.id);
    }

    this.#idleWarmHandle = null;
  }

  #openAndBindInstallments(measureName = null, detail = {}) {
    this.#openMetricDepth += 1;
    try {
      return cartPerformance.measure(measureName, () => this.#showDialog(detail));
    } finally {
      this.#openMetricDepth -= 1;
    }
  }

  #operationIdFromEvent(event) {
    return getCartEventData(event).cartAddOperationId || null;
  }

  #matchesPendingOperation(event) {
    if (!this.#pendingAdd) return true;

    const operationId = this.#operationIdFromEvent(event);
    if (this.#pendingAdd.operationId && operationId) {
      return this.#pendingAdd.operationId === operationId;
    }

    const sourceId = event.detail?.sourceId || null;
    return !this.#pendingAdd.sourceId || !sourceId || this.#pendingAdd.sourceId === sourceId;
  }

  #setPendingState(isPending, data = {}) {
    this.toggleAttribute('data-cart-add-pending', isPending);

    const dialog = this.refs.dialog;
    if (dialog instanceof HTMLElement) {
      if (isPending) {
        dialog.setAttribute('aria-busy', 'true');
      } else {
        dialog.removeAttribute('aria-busy');
      }
    }

    const cartItems = this.querySelector('cart-items');
    if (cartItems instanceof HTMLElement) {
      if (isPending) {
        cartItems.setAttribute('aria-busy', 'true');
      } else {
        cartItems.removeAttribute('aria-busy');
      }
    }

    const pendingState = this.refs.pendingState;
    if (pendingState instanceof HTMLElement) {
      pendingState.hidden = !isPending;
    }

    const pendingMessage = this.refs.pendingMessage;
    if (isPending && pendingMessage instanceof HTMLElement) {
      pendingMessage.textContent = this.#pendingMessage(data);
    }
  }

  #pendingMessage() {
    const loading = window.FitCheck?.translations?.loading;
    return typeof loading === 'string' && loading.trim() ? loading : 'Loading...';
  }

  #schedulePendingFallback() {
    if (this.#pendingClearTimer) {
      clearTimeout(this.#pendingClearTimer);
    }

    this.#pendingClearTimer = setTimeout(() => {
      this.#pendingClearTimer = null;
      this.#clearPendingState({ closeIfOpened: false, reason: 'cart-add-pending-timeout' });
    }, PENDING_TIMEOUT_MS);
  }

  #clearPendingState({ closeIfOpened = false, reason = 'cart-add-settled' } = {}) {
    if (this.#pendingClearTimer) {
      clearTimeout(this.#pendingClearTimer);
      this.#pendingClearTimer = null;
    }

    const pendingAdd = this.#pendingAdd;
    this.#pendingAdd = null;
    this.#setPendingState(false);

    if (closeIfOpened && pendingAdd?.openedByDrawer && this.refs.dialog?.open) {
      void this.closeDialog({ reason });
    }
  }

  #matchesRenderedSection(event) {
    const renderedSectionId = event.detail?.sectionId;
    if (!renderedSectionId) return true;

    const sectionIds = this.#renderSectionIds();
    return sectionIds.size ? sectionIds.has(renderedSectionId) : true;
  }

  #renderSectionIds() {
    const sectionIds = new Set();
    const cartItems = this.querySelector('cart-items[data-section-id]');

    if (cartItems instanceof HTMLElement && cartItems.dataset.sectionId) {
      sectionIds.add(cartItems.dataset.sectionId);
    }

    const section = this.closest('.shopify-section');
    const id = section instanceof HTMLElement ? section.id : '';
    if (id) {
      sectionIds.add(id.startsWith(SHOPIFY_SECTION_PREFIX) ? id.slice(SHOPIFY_SECTION_PREFIX.length) : id);
    }

    return sectionIds;
  }

  #hasCartItems() {
    const cartItems = this.querySelector('cart-items[data-fit-cart-empty]');
    return cartItems instanceof HTMLElement && cartItems.dataset.fitCartEmpty === 'false';
  }

  #isDesignMode() {
    return window.Shopify?.designMode === true;
  }

  #warmRecommendations(reason = 'cart-drawer-warm') {
    const recommendations = this.querySelector(CART_UPSELL_RECOMMENDATIONS_SELECTOR);
    if (!(recommendations instanceof HTMLElement)) return;

    if (typeof recommendations.warm === 'function') {
      recommendations.warm({ reason });
      return;
    }

    customElements
      .whenDefined('fc-recommendations')
      .then(() => {
        if (!this.isConnected || !recommendations.isConnected) return;
        recommendations.warm?.({ reason });
      })
      .catch(() => {});
  }

  #requestDeferredAcceleratedCheckout() {
    if (this.#hasRequestedDeferredAcceleratedCheckout) return this.#deferredAcceleratedCheckoutPromise;
    if (this.#isDesignMode()) return null;
    if (!this.#canRenderDeferredAcceleratedCheckout()) return null;

    this.#hasRequestedDeferredAcceleratedCheckout = true;
    this.toggleAttribute('data-deferred-accelerated-checkout-unavailable', false);
    this.toggleAttribute('data-deferred-accelerated-checkout-ready', false);
    this.toggleAttribute('data-deferred-accelerated-checkout-loading', true);
    this.#deferredAcceleratedCheckoutPromise = cartPerformance
      .measure('drawer:deferred-accelerated-checkout', () => this.#renderDeferredAcceleratedCheckout())
      .catch(() => {
        this.#markDeferredAcceleratedCheckoutUnavailable();
        return null;
      })
      .finally(() => {
        this.toggleAttribute('data-deferred-accelerated-checkout-loading', false);
      });

    return this.#deferredAcceleratedCheckoutPromise;
  }

  async #renderDeferredAcceleratedCheckout() {
    const currentCartItems = this.querySelector('cart-items[data-section-id]');
    if (!(currentCartItems instanceof HTMLElement)) return null;

    const sectionId = currentCartItems.dataset.sectionId || CART_DRAWER_AJAX_SECTION_ID;
    const markup = await sectionManager.getSectionHTML(sectionId, { cache: false });
    if (!this.isConnected) return null;
    if (!markup) {
      this.#markDeferredAcceleratedCheckoutUnavailable();
      return null;
    }

    const patched = patchCartSectionMarkup(sectionId, markup, { root: this });
    if (patched instanceof HTMLElement) {
      this.#bindInstallmentsCloseAction();
      this.#warmRecommendations('cart-deferred-accelerated-checkout');
      this.#revealDeferredAcceleratedCheckout(patched);
      patched.dispatchEvent(
        new CartSectionsRenderedEvent(null, null, { action: 'refresh', source: 'deferred-accelerated-checkout' }, { sectionId }),
      );
    } else {
      this.#markDeferredAcceleratedCheckoutUnavailable();
    }

    return patched;
  }

  #revealDeferredAcceleratedCheckout(root = this) {
    const buttons = root.querySelector(DEFERRED_ACCELERATED_CHECKOUT_SELECTOR);
    if (!(buttons instanceof HTMLElement)) {
      this.#markDeferredAcceleratedCheckoutUnavailable();
      return;
    }

    this.toggleAttribute('data-deferred-accelerated-checkout-unavailable', false);
    this.toggleAttribute('data-deferred-accelerated-checkout-ready', true);

    this.#waitForAcceleratedCheckoutHydration()
      .catch(() => {})
      .finally(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!this.isConnected || !buttons.isConnected) return;

            buttons.removeAttribute('inert');
            buttons.removeAttribute('aria-hidden');
            buttons.setAttribute('data-deferred-accelerated-checkout-visible', '');
          });
        });
      });
  }

  #markDeferredAcceleratedCheckoutUnavailable() {
    this.toggleAttribute('data-deferred-accelerated-checkout-ready', false);
    this.toggleAttribute('data-deferred-accelerated-checkout-unavailable', true);
  }

  #waitForAcceleratedCheckoutHydration() {
    const waiters = ['shopify-accelerated-checkout-cart', 'shopify-accelerated-checkout']
      .map((name) => customElements.whenDefined(name).catch(() => null));

    return Promise.race([
      Promise.all(waiters),
      new Promise((resolve) => {
        setTimeout(resolve, ACCELERATED_CHECKOUT_HYDRATION_TIMEOUT_MS);
      }),
    ]);
  }

  #canRenderDeferredAcceleratedCheckout() {
    if (!this.hasAttribute('data-defer-accelerated-checkout')) return false;
    if (this.querySelector(ACCELERATED_CHECKOUT_SELECTOR)) return false;

    const cartItems = this.querySelector('cart-items[data-section-id]');
    if (!(cartItems instanceof HTMLElement)) return false;
    if (cartItems.dataset.fitCartEmpty === 'true') return false;

    return Boolean(cartItems.dataset.sectionId);
  }

  #syncTriggerExpanded(isExpanded = this.refs.dialog?.open === true) {
    const trigger = this.refs.cartDrawerTrigger;
    if (!(trigger instanceof HTMLElement)) return;

    trigger.setAttribute('aria-expanded', String(Boolean(isExpanded)));
  }

  #bindInstallmentsCloseAction() {
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const root = this.refs.dialog instanceof HTMLElement ? this.refs.dialog : this;
      const paymentTerms = root.querySelector('shopify-payment-terms');
      const cta = paymentTerms?.shadowRoot?.querySelector('#shopify-installments-cta');

      if (!cta) return;
      cta.addEventListener('click', () => this.closeDialog(), { once: true });
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawerComponent);
}
